import { useEffect, useRef, useState } from 'react';
import { transportApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import useDashboardWebSocket from '../hooks/useDashboardWebSocket';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Bus, MapPin, Money, ArrowsClockwise } from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0) + ' FCFA';

const ParentBusTracking = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [routes, setRoutes] = useState([]);
  const [routeId, setRouteId] = useState('');
  const [routeDetail, setRouteDetail] = useState(null);
  const [location, setLocation] = useState(null);
  const [studentId, setStudentId] = useState('');
  const [payments, setPayments] = useState(null);
  const refreshTimerRef = useRef(null);

  useEffect(() => {
    transportApi.listRoutes().then(({ data }) => {
      setRoutes(data);
      if (data[0]) setRouteId(data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!routeId) return;
    transportApi.getRoute(routeId).then(({ data }) => setRouteDetail(data)).catch(() => {});
    transportApi.getLocation(routeId).then(({ data }) => setLocation(data)).catch(() => setLocation(null));
    // Poll fallback every 30s in case WebSocket is sleeping
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(() => {
      transportApi.getLocation(routeId).then(({ data }) => setLocation(data)).catch(() => {});
    }, 30000);
    return () => { if (refreshTimerRef.current) clearInterval(refreshTimerRef.current); };
  }, [routeId]);

  useEffect(() => {
    const sid = user?.student_ids?.[0];
    if (!sid) return;
    setStudentId(sid);
    transportApi.payments(sid).then(({ data }) => setPayments(data)).catch(() => {});
  }, [user]);

  // Real-time bus location via WebSocket
  useDashboardWebSocket((event, data) => {
    if (event === 'transport.location' && data.route_id === routeId) {
      setLocation(data);
    }
  });

  const lastSeen = location?.ts ? new Date(location.ts) : null;
  const minutesAgo = lastSeen ? Math.round((Date.now() - lastSeen.getTime()) / 60000) : null;
  const isLive = minutesAgo != null && minutesAgo < 2;

  const osmUrl = location
    ? `https://www.openstreetmap.org/?mlat=${location.lat}&mlon=${location.lng}#map=16/${location.lat}/${location.lng}`
    : null;
  const embedUrl = location
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${location.lng - 0.01},${location.lat - 0.01},${location.lng + 0.01},${location.lat + 0.01}&layer=mapnik&marker=${location.lat},${location.lng}`
    : null;

  return (
    <div className="space-y-6" data-testid="parent-bus-tracking">
      <div className="flex items-center gap-3">
        <Bus size={28} className="text-[#0052FF]" weight="duotone" />
        <h1 className="font-outfit text-2xl font-bold">{t('busTracking')}</h1>
      </div>

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4">
          <label className="text-xs uppercase text-[#64748B]">{t('transport')}</label>
          <select
            className="w-full border border-[#E2E8F0] rounded-md p-2 mt-1 text-sm"
            value={routeId}
            onChange={e => setRouteId(e.target.value)}
            data-testid="parent-route-select"
          >
            <option value="">{t('pickRoute')}</option>
            {routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </CardContent>
      </Card>

      {routeDetail && (
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader>
            <CardTitle className="font-outfit text-base flex items-center justify-between">
              <span className="flex items-center gap-2"><MapPin size={18} /> {t('busPosition')}</span>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${isLive ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid="bus-live-badge">
                {isLive ? `● ${t('live')}` : location ? t('minutesAgo').replace('{n}', minutesAgo) : t('offline')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {embedUrl ? (
              <div className="rounded overflow-hidden border border-[#E2E8F0]" data-testid="bus-map">
                <iframe
                  title={t('busPosition')}
                  src={embedUrl}
                  style={{ width: '100%', height: 300, border: 0 }}
                  loading="lazy"
                />
                <div className="bg-[#F8FAFC] p-2 text-xs flex items-center justify-between">
                  <span className="text-[#64748B]">{location.lat.toFixed(5)}, {location.lng.toFixed(5)}</span>
                  <a href={osmUrl} target="_blank" rel="noreferrer" className="text-[#0052FF] hover:underline">{t('openInOSM')}</a>
                </div>
              </div>
            ) : (
              <div className="text-center p-6 text-sm text-[#94A3B8] bg-[#F8FAFC] rounded">
                <ArrowsClockwise size={24} className="mx-auto mb-2 animate-spin opacity-50" />
                {t('waitingGps')}
              </div>
            )}

            {(routeDetail.stops || []).length > 0 && (
              <div>
                <p className="text-xs uppercase text-[#64748B] mb-1">{t('stops')}</p>
                <ol className="text-sm space-y-1">
                  {routeDetail.stops.map((st, i) => (
                    <li key={i} className="flex gap-2 items-center">
                      <span className="bg-[#0052FF] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{i + 1}</span>
                      <span className="flex-1">{st.name}</span>
                      <span className="text-xs text-[#64748B]">{st.time || ''}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {payments && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="parent-transport-payments">
          <CardHeader><CardTitle className="font-outfit text-base flex items-center gap-2">
            <Money size={18} className="text-[#0052FF]" /> {t('transportFees')}
          </CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              <div><p className="text-xs text-[#64748B]">{t('total')}</p><p className="font-bold">{fmt(payments.transport_total)}</p></div>
              <div><p className="text-xs text-[#64748B]">{t('paid')}</p><p className="font-bold text-green-600">{fmt(payments.transport_paid)}</p></div>
              <div><p className="text-xs text-[#64748B]">{t('balance')}</p><p className="font-bold text-red-600">{fmt(payments.transport_balance)}</p></div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ParentBusTracking;
